import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import NavigationBar from "../components/Navigation";
import Footer from "../components/Footer";
import UserXpAvatar from "./UserXpAvatar";
import {
    collection,
    getDocs,
    getDoc,
    query,
    orderBy,
    doc,
    updateDoc,
    serverTimestamp,
    increment,
    writeBatch,
    limit,
    startAfter,
} from "firebase/firestore";
import { db } from "../config/firebasedeconfig";
import "../styles/components/SubjectPage.scss";
import { UserContext } from "../UserContext";
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";
import { defaultAvatarUrl, defaultUsername, defaultEmail } from '../constants';
import { OnlineUsersContext } from "../OnlineUsersContext";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getUserActivityScore, XP_WEIGHTS } from '../components/levelUtils';
import ActivityGainToast from "./ActivityGainToast";
import animationData_Stvalentin_pulse2 from '../lotties/st-Valentin-heart-pulse_01.json';
import Lottie from "lottie-react";
import BackToTopButton from '../components/BackToTopButton';
import notificationService from '../services/notificationService';
import { useLanguage } from '../contexts/LanguageContext';
import { getForumQuotaStatus } from "../utils/forumQuotaUtils";
import ForumQuotaStatus from "../components/ForumQuotaStatus";
import {
    setForumLike,
    deleteForumReply,
} from "../services/forumActivityService";

const REPLIES_PAGE_SIZE = 10;

const formatActivityGainPoints = (value) => {
    const number = Number(value) || 0;

    return `+${Number.isInteger(number)
        ? number
        : number.toFixed(2).replace(/\.?0+$/, "")
        }`;
};

const SubjectPage = () => {
    const { t, currentLanguage } = useLanguage();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        username,
        userId,
        userData,
        messageCount,
        updateMessageCount,
        fetchUserMessageCount
    } = useContext(UserContext);
    const { onlineUsers } = useContext(OnlineUsersContext);
    const [subject, setSubject] = useState(null);
    const [replies, setReplies] = useState([]);
    const [newReply, setNewReply] = useState("");
    const [lastVisibleReply, setLastVisibleReply] = useState(null);
    const [hasMoreReplies, setHasMoreReplies] = useState(false);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [error, setError] = useState("");
    const [isHovered, setIsHovered] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [tmdbPosterPathFallback, setTmdbPosterPathFallback] = useState(null);
    const [subjectNotFound, setSubjectNotFound] = useState(false);
    const [activityGainToast, setActivityGainToast] = useState(null);
    const [isSubmittingReply, setIsSubmittingReply] = useState(false);
    const [deletingReplyId, setDeletingReplyId] = useState(null);
    const deletingReplyRef = React.useRef(false);
    const [forumQuotaRefreshKey, setForumQuotaRefreshKey] = useState(0);
    const targetReplyId =
        location.state?.replyId ||
        new URLSearchParams(location.search).get("replyId") ||
        (location.hash?.startsWith("#reply-")
            ? location.hash.replace("#reply-", "")
            : "");


    // Active le mode test pourle système de titre en mettant `true` à cette variable
    // const debugTestMode = true;
    const debugTestMode = false;

    /* Aspirant Figurant(0 à 9)
    Cadreur Novice(10 à 49)
    Apprenti Monteur(50 à 99)
    Assistant Réalisateur(100 à 199)
    Scénariste Prometteur(200 à 499)
    Chef Opérateur Illumineur(500 à 999)
    Réalisateur Émérite(1000 à 1999)
    Maître de la Mise en Scène(2000 à 3999)
    Légende du 7e Art(4000 à 6999)
    Divinité de la Pellicule(7000 et +)
     */

    // const testMessageCount = 5;    // "Aspirant Figurant"
    // const testMessageCount = 13;    // "Cadreur Novice" *
    // const testMessageCount = 50;    // "Apprenti Monteur"
    // const testMessageCount = 100;   // "Assistant Réalisateur"*   
    // const testMessageCount = 200;   // "Scénariste Prometteur"*
    // const testMessageCount = 500;   // "Chef Opérateur Illumineur"*
    // const testMessageCount = 1000;  // "Réalisateur Émérite"*  
    // const testMessageCount = 2000;  // "Maître de la Mise en Scène"
    // const testMessageCount = 4000;  // "Légende du 7e Art"
    const testMessageCount = 7500;  // "Divinité de la Pellicule"
    const displayMessageCount = debugTestMode ? testMessageCount : messageCount;

    const showLikeGivenActivityToast = () => {
        setActivityGainToast({
            id: `like-given-${Date.now()}`,
            points: formatActivityGainPoints(XP_WEIGHTS.likesGiven),
            unitLabel: t("profilMember.activity"),
            label: t("activityToast.likeGiven"),
            variant: "like",
        });
    };


    const showReplyCreatedActivityToast = () => {
        setActivityGainToast({
            id: `reply-created-${Date.now()}`,
            points: formatActivityGainPoints(XP_WEIGHTS.replies),
            unitLabel: t("profilMember.activity"),
            label: t("activityToast.replyCreated"),
            variant: "reply",
        });
    };

    // Fonction pour supprimer une réponse avec un toast de succès
    const handleDeleteReply = async (replyId) => {
        if (deletingReplyRef.current) return;

        deletingReplyRef.current = true;
        setDeletingReplyId(replyId);

        try {
            await deleteForumReply({ postId: id, replyId });

            // Retire la réponse à l'écran après confirmation du serveur.
            setReplies((currentReplies) =>
                currentReplies.filter((reply) => reply.id !== replyId)
            );

            toast.success(t('forum.subjectPage.deleteReplySuccess'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            await Promise.all([
                fetchReplies({ reset: true }),
                fetchSubject(),
            ]);
        } catch (err) {
            toast.error(t('forum.subjectPage.deleteReplyError'));
            console.error("Erreur :", err);
        } finally {
            deletingReplyRef.current = false;
            setDeletingReplyId(null);
        }
    };

    // Fonction pour modifier une réponse avec un toast de succès
    const handleEditReply = async (replyId, newContent) => {
        try {
            await updateDoc(doc(db, 'posts', id, 'replies', replyId), {
                content: newContent,
                updatedAt: new Date(),
            });
            toast.success(t('forum.subjectPage.editReplySuccess'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            fetchReplies({ reset: true }); // Recharge les réponses après modification
        } catch (err) {
            toast.error(t('forum.subjectPage.editReplyError'));
            console.error("Erreur :", err);
        }
    };


    // Import des modules nécessaires pour les couleur quill
    const Font = Quill.import("formats/font");
    Font.whitelist = null; // Ajoutez vos polices autorisées si nécessaire
    Quill.register(Font, true);

    // Modules pour ReactQuill
    const modules = {
        toolbar: [
            [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['clean'],
        ],
    };

    const formats = [
        "header",
        "bold",
        "italic",
        "underline",
        "strike",
        "color",
        "background",
        "align",
        "link",
        "image",
        "video",
    ];

    const fetchSubject = async () => {
        try {
            const subjectDoc = doc(db, "posts", id);
            const subjectSnapshot = await getDoc(subjectDoc);

            if (!subjectSnapshot.exists()) {
                console.log("Le sujet n'existe pas.");
                setSubject(null);
                setReplies([]);
                setSubjectNotFound(true);
                return;
            }

            setSubjectNotFound(false);

            const subjectData = {
                id: subjectSnapshot.id,
                ...subjectSnapshot.data(),
                likes: subjectSnapshot.data().likes || [], // Initialisez likes ici
            };

            // Récupérer les informations des utilisateurs ayant liké
            subjectData.likeUsernames = await fetchLikeUsernames(subjectData.likes);

            // Récupérer les informations de l'auteur
            if (subjectData.authorId) {
                const userDocRef = doc(db, "users", subjectData.authorId);
                const userDoc = await getDoc(userDocRef);

                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    subjectData.avatar = userData.avatar || defaultAvatarUrl;
                    subjectData.author = userData.username || t('forum.userUnknown');
                    const authorMessageCount = await fetchUserMessageCount(subjectData.authorId);

                    subjectData.messageCount = authorMessageCount;
                    subjectData.activityScore = getUserActivityScore(userData, authorMessageCount);
                }
            } else {
                subjectData.avatar = defaultAvatarUrl;
                subjectData.author = t('forum.authorUnknown');
            }

            setSubject(subjectData);
        } catch (error) {
            console.error("Erreur lors de la récupération du sujet :", error);
        }
    };

    useEffect(() => {
        const scrollTimeout = window.setTimeout(() => {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: "auto",
            });
        }, 0);

        return () => window.clearTimeout(scrollTimeout);
    }, [id]);

    // Charger les détails du sujet
    useEffect(() => {
        fetchSubject();
    }, [id]);

    const enrichReplyWithAuthor = async (docSnap) => {
        const reply = { id: docSnap.id, ...docSnap.data() };

        if (reply.authorId) {
            const userRef = doc(db, "users", reply.authorId);
            const userDoc = await getDoc(userRef);

            let replyUserData = {};

            if (userDoc.exists()) {
                replyUserData = userDoc.data();
                reply.avatar = replyUserData.avatar || defaultAvatarUrl;
                reply.authorName = replyUserData.username || t('forum.userUnknown');
            } else {
                reply.avatar = defaultAvatarUrl;
                reply.authorName = t('forum.authorUnknown');
            }

            const replyMessageCount = await fetchUserMessageCount(reply.authorId);

            reply.messageCount = replyMessageCount;
            reply.activityScore = getUserActivityScore(replyUserData, replyMessageCount);
        } else {
            reply.avatar = defaultAvatarUrl;
            reply.authorName = t('forum.authorUnknown');
            reply.messageCount = 0;
            reply.activityScore = 0;
        }

        reply.likes = Array.isArray(reply.likes) ? reply.likes : [];
        reply.likeUsernames = await fetchLikeUsernames(reply.likes);

        return reply;
    };

    // Chargement de pages..
    const fetchReplies = async ({ reset = true } = {}) => {
        try {
            setLoadingReplies(true);

            const subjectRef = doc(db, "posts", id);
            const subjectDoc = await getDoc(subjectRef);

            if (!subjectDoc.exists()) {
                setSubject(null);
                setReplies([]);
                setSubjectNotFound(true);
                setLastVisibleReply(null);
                setHasMoreReplies(false);
                return;
            }

            const repliesQuery = query(
                collection(db, "posts", id, "replies"),
                orderBy("timestamp", "asc"),
                limit(REPLIES_PAGE_SIZE + 1)
            );

            const querySnapshot = await getDocs(repliesQuery);

            if (querySnapshot.empty) {
                setReplies([]);
                setLastVisibleReply(null);
                setHasMoreReplies(false);
                return;
            }

            const visibleDocs = querySnapshot.docs.slice(0, REPLIES_PAGE_SIZE);
            const hasNextPage = querySnapshot.docs.length > REPLIES_PAGE_SIZE;
            const lastDoc = visibleDocs[visibleDocs.length - 1] || null;

            const repliesData = await Promise.all(
                visibleDocs.map(enrichReplyWithAuthor)
            );

            setReplies(repliesData);
            setLastVisibleReply(lastDoc);
            setHasMoreReplies(hasNextPage);
        } catch (error) {
            console.error("Erreur lors de la récupération des réponses :", error);
        } finally {
            setLoadingReplies(false);
        }
    };

    const loadMoreReplies = async () => {
        if (!lastVisibleReply || loadingReplies) return;

        try {
            setLoadingReplies(true);

            const repliesQuery = query(
                collection(db, "posts", id, "replies"),
                orderBy("timestamp", "asc"),
                startAfter(lastVisibleReply),
                limit(REPLIES_PAGE_SIZE + 1)
            );

            const querySnapshot = await getDocs(repliesQuery);

            if (querySnapshot.empty) {
                setHasMoreReplies(false);
                setLastVisibleReply(null);
                return;
            }

            const visibleDocs = querySnapshot.docs.slice(0, REPLIES_PAGE_SIZE);
            const hasNextPage = querySnapshot.docs.length > REPLIES_PAGE_SIZE;
            const lastDoc = visibleDocs[visibleDocs.length - 1] || null;

            const repliesData = await Promise.all(
                visibleDocs.map(enrichReplyWithAuthor)
            );

            setReplies((prevReplies) => [...prevReplies, ...repliesData]);
            setLastVisibleReply(lastDoc);
            setHasMoreReplies(hasNextPage);
        } catch (error) {
            console.error("Erreur lors du chargement des réponses suivantes :", error);
        } finally {
            setLoadingReplies(false);
        }
    };

    useEffect(() => {
        fetchReplies({ reset: true });
    }, [id]);

    useEffect(() => {
        if (!targetReplyId || replies.length === 0) {
            return;
        }

        const scrollTimeout = window.setTimeout(() => {
            const targetElement = document.getElementById(`reply-${targetReplyId}`);

            if (!targetElement) {
                return;
            }

            const nav = document.querySelector(".navigation");
            const navHeight = nav ? nav.getBoundingClientRect().height : 0;
            const extraOffset = window.innerWidth <= 992 ? 130 : 60;

            const targetTop =
                targetElement.getBoundingClientRect().top +
                window.pageYOffset -
                navHeight -
                extraOffset;

            window.scrollTo({
                top: Math.max(targetTop, 0),
                behavior: "smooth",
            });

            targetElement.classList.add("reply--notification-target");

            window.setTimeout(() => {
                targetElement.classList.remove("reply--notification-target");
            }, 3500);
        }, 450);

        return () => window.clearTimeout(scrollTimeout);
    }, [targetReplyId, replies]);

    const isMovieReviewSubject =
        subject?.type === "movie_rating_discussion" ||
        subject?.source === "movie_rating_review";

    const subjectMovieId = subject?.tmdbId || subject?.movieRatingKey;

    const subjectMoviePosterPath =
        subject?.moviePosterPath || tmdbPosterPathFallback;

    const subjectMoviePosterUrl = subjectMoviePosterPath
        ? `https://image.tmdb.org/t/p/w185${subjectMoviePosterPath}`
        : null;


    useEffect(() => {
        const fetchMissingMoviePoster = async () => {
            if (!isMovieReviewSubject || !subjectMovieId || subject?.moviePosterPath) {
                setTmdbPosterPathFallback(null);
                return;
            }

            const normalizedMovieId = Number(subjectMovieId);

            if (!Number.isFinite(normalizedMovieId)) {
                return;
            }

            try {
                const tmdbLanguage =
                    currentLanguage === "en"
                        ? "en-US"
                        : `${currentLanguage}-${currentLanguage.toUpperCase()}`;

                const response = await axios.get(
                    `https://api.themoviedb.org/3/movie/${normalizedMovieId}?api_key=e16900047627f56531a2d3d8d4df9e92&language=${tmdbLanguage}`
                );

                const posterPath = response.data?.poster_path || "";
                const backdropPath = response.data?.backdrop_path || "";
                const movieTitle =
                    response.data?.title ||
                    subject?.movieTitle ||
                    subject?.title ||
                    "";

                if (!posterPath) {
                    return;
                }

                setTmdbPosterPathFallback(posterPath);

                try {
                    await updateDoc(doc(db, "posts", id), {
                        moviePosterPath: posterPath,
                        movieBackdropPath: backdropPath,
                        movieTitle,
                        tmdbId: normalizedMovieId,
                        movieRatingKey: String(normalizedMovieId),
                    });
                } catch (updateError) {
                    console.warn(
                        "[MovieFrenzy] Vignette TMDB trouvée, mais impossible de l’enregistrer dans Firestore :",
                        updateError
                    );
                }
            } catch (error) {
                console.error(
                    "[MovieFrenzy] Impossible de récupérer la vignette TMDB du sujet :",
                    error
                );
            }
        };

        fetchMissingMoviePoster();
    }, [
        id,
        isMovieReviewSubject,
        subjectMovieId,
        subject?.moviePosterPath,
        subject?.movieTitle,
        subject?.title,
        currentLanguage,
    ]);


    // Ajouter une nouvelle réponse
    const handleReplySubmit = async (e) => {
        e.preventDefault();

        if (!newReply || newReply === "<p><br></p>") {
            setError(t('forum.subjectPage.errorContentRequired'));
            return;
        }

        if (!userId) {
            setError(t("forumQuota.errorUserNotFound"));
            return;
        }

        if (isSubmittingReply) {
            return;
        }

        setIsSubmittingReply(true);

        try {
            const quotaStatus = await getForumQuotaStatus({
                userId,
                subscription: userData?.subscription,
            });

            if (!quotaStatus.allowed) {
                if (quotaStatus.reason === "daily-limit") {
                    setError(
                        t("forumQuota.dailyLimit", {
                            limit: quotaStatus.limits.daily,
                        })
                    );
                } else if (
                    quotaStatus.reason === "rolling-30-days-limit"
                ) {
                    setError(
                        t("forumQuota.rolling30DaysLimit", {
                            limit: quotaStatus.limits.rolling30Days,
                        })
                    );
                } else {
                    setError(t("forumQuota.unavailable"));
                }

                return;
            }
            const replyRef = doc(
                collection(db, "posts", id, "replies")
            );

            const usageRef = doc(
                collection(db, "users", userId, "forumUsage")
            );

            const subjectRef = doc(db, "posts", id);
            const userRef = doc(db, "users", userId);

            const batch = writeBatch(db);

            batch.set(replyRef, {
                author: username || t('forum.anonymousUser'),
                authorId: userId,
                content: newReply,
                timestamp: serverTimestamp(),
                likes: [],
            });

            batch.set(usageRef, {
                type: "reply",
                postId: id,
                replyId: replyRef.id,
                createdAt: serverTimestamp(),
            });

            batch.update(subjectRef, {
                replies: increment(1),
                lastActivityAt: serverTimestamp(),
                lastActivityBy: userId,
                lastActivityType: "reply",
            });

            batch.update(userRef, {
                "xp.sources.replies": increment(1),
                "xp.updatedAt": serverTimestamp(),
            });

            await batch.commit();
            setForumQuotaRefreshKey((prev) => prev + 1);
            showReplyCreatedActivityToast();
            setNewReply("");
            setError("");
            fetchReplies({ reset: true });
            fetchSubject();

            if (userId !== subject.authorId) {
                const subjectTitle = subject.title;

                try {
                    await notificationService.createTopicReplyNotification(
                        subject.authorId,
                        userId,
                        username || t('forum.userUnknown'),
                        id,
                        subjectTitle
                    );
                    console.log("Notification de réponse créée avec succès");
                } catch (notifError) {
                    console.error("Erreur lors de la création de la notification de réponse:", notifError);
                }
            }
        } catch (err) {
            console.error(
                "Erreur lors de l'ajout de la réponse :",
                err
            );
        } finally {
            setIsSubmittingReply(false);
        }
    };

    // Fonction pour annuler l'ajout de la nouvelle réponse
    const handleCancel = () => {
        navigate("/Forum"); // Redirige vers la page Forum.js
    };

    // Fonction pour liker une réponse
    const handleLike = async (replyId) => {
        if (!userId) {
            toast.error("Utilisateur introuvable. Veuillez vous reconnecter.");
            return;
        }

        const replyRef = doc(db, "posts", id, "replies", replyId);

        try {
            const replyDoc = await getDoc(replyRef);

            if (!replyDoc.exists()) {
                toast.error(t('forum.subjectPage.likeError'));
                return;
            }

            const replyData = replyDoc.data();
            const currentLikes = Array.isArray(replyData.likes)
                ? replyData.likes
                : [];

            const wasLiked = currentLikes.includes(userId);

            const result = await setForumLike({
                postId: id,
                replyId,
                liked: !wasLiked,
            });

            if (
                result.changed &&
                result.liked &&
                result.authorId &&
                userId !== result.authorId
            ) {
                showLikeGivenActivityToast();

                try {
                    await notificationService.createReplyLikeNotification(
                        result.authorId,
                        userId,
                        username || t('forum.userUnknown'),
                        id,
                        subject.title,
                        replyId
                    );
                } catch (notifError) {
                    console.error(
                        "Erreur lors de la création de la notification de like :",
                        notifError
                    );
                }
            }

            fetchReplies({ reset: true });
        } catch (err) {
            console.error("Erreur lors du like :", err);
            toast.error(t('forum.subjectPage.likeError'));
        }
    };

    // Fonction pour liker le sujet 
    const [isLiking, setIsLiking] = useState(false);

    const handleSubjectLike = async () => {
        if (!userId) {
            toast.error("Utilisateur introuvable. Veuillez vous reconnecter.");
            return;
        }

        if (isLiking) return;
        setIsLiking(true);

        const subjectRef = doc(db, "posts", id);

        try {
            const subjectDoc = await getDoc(subjectRef);

            if (!subjectDoc.exists()) {
                setSubject(null);
                setReplies([]);
                setSubjectNotFound(true);
                return;
            }

            setSubjectNotFound(false);

            const subjectData = subjectDoc.data();
            const currentLikes = Array.isArray(subjectData.likes)
                ? subjectData.likes
                : [];

            const wasLiked = currentLikes.includes(userId);

            const result = await setForumLike({
                postId: id,
                liked: !wasLiked,
            });

            if (
                result.changed &&
                result.liked &&
                result.authorId &&
                userId !== result.authorId
            ) {
                showLikeGivenActivityToast();

                try {
                    await notificationService.createReplyLikeNotification(
                        result.authorId,
                        userId,
                        username || t('forum.userUnknown'),
                        id,
                        subjectData.title
                    );
                } catch (notifError) {
                    console.error(
                        "Erreur lors de la création de la notification de like de sujet :",
                        notifError
                    );
                }
            }

            fetchSubject();
        } catch (err) {
            console.error("Erreur lors du like du sujet :", err);
            toast.error(t('forum.subjectPage.likeError'));
        } finally {
            setIsLiking(false);
        }
    };


    // Fonction pour récupérer les noms des utilisateurs ayant liké le sujet
    const fetchLikeUsernames = async (likeIds) => {
        if (!likeIds || !Array.isArray(likeIds) || likeIds.length === 0) return [];
        return await Promise.all(
            likeIds
                .filter((userId) => userId != null) // On élimine les valeurs nulles ou undefined
                .map(async (userId) => {
                    try {
                        const userRef = doc(db, "users", userId);
                        const userDoc = await getDoc(userRef);
                        return userDoc.exists() ? userDoc.data().username : t('forum.userUnknown');
                    } catch (err) {
                        console.error(err);
                        return t('forum.userUnknown');
                    }
                })
        );
    };

    if (subjectNotFound) {
        return (
            <>
                <ToastContainer />
                <div
                    className="forum-page"
                    style={{
                        backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundAttachment: "fixed",
                        backgroundBlendMode: "overlay",
                        backgroundColor: "#200211",
                        opacity: "0.9",
                        overflowX: "hidden",
                        overflowY: "auto",
                        minHeight: "100vh",
                    }}
                >
                    <NavigationBar />

                    <ActivityGainToast
                        visible={Boolean(activityGainToast)}
                        toastId={activityGainToast?.id}
                        points={activityGainToast?.points}
                        unitLabel={activityGainToast?.unitLabel}
                        label={activityGainToast?.label}
                        variant={activityGainToast?.variant}
                        onDone={() => setActivityGainToast(null)}
                    />

                    <div className="subject-page">
                        <div className="subject-not-found">
                            <h2>Ce sujet n’existe plus ou a été supprimé.</h2>
                            <p>Il n’est plus possible d’afficher cette discussion.</p>

                            <button
                                type="button"
                                className="subject-not-found__button"
                                onClick={() => navigate("/Forum")}
                            >
                                Retour au forum
                            </button>
                        </div>
                    </div>
                </div>

                <div>
                    <BackToTopButton />
                </div>

                <div className="footer">
                    <Footer />
                </div>
            </>
        );
    }

    return (
        <>
            <ToastContainer />

            <ActivityGainToast
                visible={Boolean(activityGainToast)}
                toastId={activityGainToast?.id}
                points={activityGainToast?.points}
                unitLabel={activityGainToast?.unitLabel}
                label={activityGainToast?.label}
                variant={activityGainToast?.variant}
                onDone={() => setActivityGainToast(null)}
            />

            <div
                className="forum-page"
                style={{
                    backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundAttachment: "fixed",
                    backgroundBlendMode: "overlay",
                    backgroundColor: "#200211",
                    opacity: "0.9",
                    overflowX: "hidden",
                    overflowY: "auto",
                    minHeight: "100vh",
                }}
            >
                <NavigationBar />

                <div className="subject-page"
                    style={{
                        marginTop: "1.25rem",
                        padding: "1.5rem",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "0.5rem",
                        boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
                        border: "2px solid #00FFFF",

                    }}
                >
                    {subject ? (
                        <div className="subject-container"
                            style={{
                                marginBottom: "1.5rem",
                                padding: "1.5rem",
                                backgroundColor: "rgba(0, 0, 0, 0.6)", // Fond plus opaque pour une meilleure lisibilité
                                borderRadius: "0.5rem",
                                boxShadow:
                                    "0 0.125rem 0.5rem rgba(0, 0, 0, 0.7), inset 0 0.125rem 0.25rem rgba(0, 0, 0, 0.2)", // Ombre externe et ombre intérieure plus discrète
                                border: "2px solid #00FFFF", // Bordure unifiée en #00FFFF
                            }}
                        >
                            <div className="subject-movie-review-header">
                                <div className="subject-movie-review-header__text">
                                    <h1>{subject.title}</h1>
                                    <p dangerouslySetInnerHTML={{ __html: subject.content }}></p>
                                </div>

                                {isMovieReviewSubject && subjectMovieId && subjectMoviePosterUrl && (
                                    <button
                                        type="button"
                                        className="subject-movie-card-link"
                                        onClick={() => navigate(`/ficheFilm/${subjectMovieId}`)}
                                        title={`Retourner à la fiche de ${subject.movieTitle || subject.title}`}
                                    >
                                        <img
                                            src={subjectMoviePosterUrl}
                                            alt={subject.movieTitle || subject.title}
                                            className="subject-movie-card-link__poster"
                                        />
                                        <span className="subject-movie-card-link__label">
                                            Voir la fiche
                                        </span>
                                    </button>
                                )}
                            </div>
                            <div className="subject-author"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginTop: "0.5rem",
                                    gap: "0.5rem",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    justifyContent: "space-between",
                                    backgroundColor: "rgba(0, 0, 0, 0.7)", // Fond un peu plus foncé pour accentuer le contraste avec le texte
                                    padding: "0.75rem",
                                    borderRadius: "0.5rem",
                                    border: "1px solid #00FFFF",
                                }}
                            >
                                <div className="avatar-containerForumInfo" style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: "1" }}>
                                    <span><p>{t('forum.subjectPage.publishedBy')}{" "}<span style={{ fontWeight: "bold", color: "#00FFFF", marginLeft: "0.25rem" }}>{subject.author}</span></p></span>
                                    <span><p>{t('forum.subjectPage.at')}{" "}<span style={{ fontWeight: "bold", color: "#FF33CC", marginLeft: "0.25rem" }}>{new Date(subject.timestamp?.seconds * 1000).toLocaleString()}</span></p></span>
                                    {/* Bouton de like */}
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <button
                                            className={`heart-buttonReply ${subject.likes && subject.likes.includes(userId) ? "liked" : ""}`}
                                            onClick={handleSubjectLike}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "0.5rem",
                                                fontSize: "1.5rem",
                                                marginTop: "0.5rem",
                                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                borderRadius: "0.5rem",
                                                padding: "0.5rem",
                                                boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
                                                border: "2px solid #D90093"
                                            }}
                                        >
                                            <Lottie animationData={animationData_Stvalentin_pulse2} style={{ width: "3.5rem", height: "3.5rem" }} /> <span style={{ fontWeight: "bold", color: "#00FFFF", marginRight: "0.5rem" }}>{subject.likes ? subject.likes.length : 0}</span>
                                        </button>
                                    </div>
                                    {subject.likeUsernames && subject.likeUsernames.length > 0 && (
                                        <div className="like-list" style={{ marginTop: "0.5rem" }}>
                                            <p style={{ fontWeight: "bold", color: "#00FFFF", marginLeft: "0.25rem" }}>{t('forum.subjectPage.likedBy')}{" "}<span style={{ fontWeight: "Light!", color: "#FFFFFF", marginLeft: "0.25rem", textShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)" }}>{subject.likeUsernames.join(", ")}</span></p>
                                        </div>
                                    )}
                                </div>
                                <div className="avatar-containerForumAvatarInfo" style={{ color: "#00FFFF", fontSize: "0.8rem", backgroundColor: "#200211", padding: "0.5rem", borderRadius: "0.25rem", boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)", flex: "1", border: "2px solid #D90093", minWidth: "50vw", width: "50vw" }}>
                                    <div className="avatar-containerForumHome"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            marginTop: "0.5rem",
                                            gap: "0.5rem",
                                            flexDirection: "row",
                                        }}
                                    >
                                        <UserXpAvatar
                                            avatarUrl={subject.avatar || defaultAvatarUrl}
                                            activityScore={
                                                debugTestMode
                                                    ? testMessageCount
                                                    : (subject.activityScore ?? subject.messageCount ?? 0)
                                            }
                                            fallbackActivityScore={subject.messageCount}
                                            titleLabel={t("forum.levelTitle")}
                                            activityLabel={t("forum.activityScore")}
                                            className="user-xp-avatar--subject-main"
                                            userId={subject.authorId}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p>{t('forum.subjectPage.loading')}</p>
                    )}
                    <div className="replies">
                        <h2>{t('forum.subjectPage.repliesTitle')}</h2>
                        {/* Afficher les réponses les plus anciennes en premier */}
                        {replies.map(reply => (
                            <div
                                key={reply.id}
                                id={`reply-${reply.id}`}
                                className={`reply ${targetReplyId === reply.id ? "reply--targeted-by-notification" : ""}`}
                                style={{
                                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                                    padding: "1rem",
                                    marginBottom: "1rem",
                                    borderRadius: "0.5rem",
                                    boxShadow:
                                        "0 0.125rem 0.5rem rgba(0, 0, 0, 0.7), inset 0 0.125rem 0.25rem rgba(0, 0, 0, 0.2)",
                                    border: "2px solid #00FFFF",
                                }}
                            >
                                <div className="reply-author">
                                    <p dangerouslySetInnerHTML={{ __html: reply.content }}></p>
                                    {reply.updatedAt && (
                                        <small style={{ fontStyle: 'italic', color: 'red', marginBottom: "0.5rem", display: "block" }}>
                                            {t('forum.subjectPage.modifiedOn')}{" "}{new Date(reply.updatedAt.seconds * 1000).toLocaleString()}
                                        </small>
                                    )}
                                    <div
                                        className="reply-author_description"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            marginTop: "0.5rem",
                                            backgroundColor: "rgba(0, 0, 0, 0.4)",
                                            borderRadius: "0.5rem",
                                            padding: "0.5rem",
                                            boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5), inset 0 0.125rem 0.25rem #00FFFF",
                                            border: "1px solid #00FFFF"
                                        }}
                                    >
                                        <span style={{ display: "flex", flexDirection: "column", marginLeft: "0.5rem", flex: "1" }}>
                                            <p>
                                                {t('forum.subjectPage.publishedBy')}{" "}
                                                <span style={{ fontWeight: "bold", color: "#00FFFF", marginLeft: "0.25rem" }}>
                                                    {reply.author}
                                                </span>
                                            </p>
                                            <p>
                                                {t('forum.subjectPage.at')}{" "}
                                                <span style={{ fontWeight: "bold", color: "#FF33CC", marginLeft: "0.25rem" }}>
                                                    {new Date(reply.timestamp?.seconds * 1000).toLocaleString()}
                                                </span>
                                            </p>
                                            {/* Bouton de like */}
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                <button
                                                    className={`heart-buttonReply ${reply.likes && reply.likes.includes(userId) ? "liked" : ""}`}
                                                    onClick={() => handleLike(reply.id)}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "0.5rem",
                                                        fontSize: "1.5rem",
                                                        marginTop: "0.5rem",
                                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                        borderRadius: "0.5rem",
                                                        padding: "0.5rem",
                                                        boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
                                                        border: "2px solid #D90093"
                                                    }}
                                                >
                                                    <Lottie animationData={animationData_Stvalentin_pulse2} style={{ width: "3.5rem", height: "3.5rem" }} /> <span style={{ fontWeight: "bold", color: "#00FFFF", marginRight: "0.5rem" }}>{reply.likes ? reply.likes.length : 0}</span>
                                                </button>
                                            </div>
                                            {reply.likeUsernames && reply.likeUsernames.length > 0 && (
                                                <div className="like-list" style={{ marginTop: "0.5rem" }}>
                                                    <p style={{ fontWeight: "bold", color: "#00FFFF", marginLeft: "0.25rem" }}>{t('forum.subjectPage.likedBy')}{" "}<span style={{ fontWeight: "Light", color: "#FFFFFF", marginLeft: "0.25rem", textShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)" }}>{reply.likeUsernames.join(", ")}</span></p>
                                                </div>
                                            )}
                                        </span>
                                        {reply.authorId === userId && (
                                            <div
                                                className="reply-actionsSubjectPage"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginRight: "2.5rem",
                                                    flex: "0.575"
                                                }}
                                            >
                                                <button
                                                    onClick={() => handleDeleteReply(reply.id)}
                                                    disabled={deletingReplyId !== null}
                                                    aria-busy={deletingReplyId === reply.id}
                                                    style={{
                                                        marginRight: "0.5rem",
                                                        opacity: deletingReplyId !== null ? 0.6 : 1,
                                                        cursor: deletingReplyId !== null ? "wait" : "pointer",
                                                    }}
                                                >
                                                    {deletingReplyId === reply.id ? "⏳ " : ""}
                                                    {t('forum.delete')}
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/edit-reply/${id}/${reply.id}`)}
                                                    style={{ marginRight: "0.5rem" }}
                                                >
                                                    {t('forum.edit')}
                                                </button>
                                            </div>
                                        )}
                                        <div
                                            className="base_avatar-containerForumHome"
                                            style={{
                                                color: "#00FFFF",
                                                fontSize: "0.8rem",
                                                backgroundColor: "#200211",
                                                padding: "0.5rem",
                                                borderRadius: "0.25rem",
                                                boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
                                                flex: "1",
                                                minWidth: "15rem",
                                                border: "1px solid #00FFFF"
                                            }}
                                        >
                                            <div
                                                className="avatar-containerForumHomeImg"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    marginTop: "0.5rem",
                                                    gap: "0.5rem",
                                                    flexDirection: "row"
                                                }}
                                            >
                                                <UserXpAvatar
                                                    avatarUrl={reply.avatar || defaultAvatarUrl}
                                                    activityScore={
                                                        debugTestMode
                                                            ? testMessageCount
                                                            : (reply.activityScore ?? reply.messageCount ?? 0)
                                                    }
                                                    fallbackActivityScore={reply.messageCount}
                                                    titleLabel={t("forum.levelTitle")}
                                                    activityLabel={t("forum.activityScore")}
                                                    className="user-xp-avatar--subject-reply"
                                                    userId={reply.authorId}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {hasMoreReplies && (
                            <button
                                type="button"
                                onClick={loadMoreReplies}
                                disabled={loadingReplies}
                                className="buttonForumChargerMore"
                                style={{
                                    display: "block",
                                    margin: "1.5rem auto",
                                    padding: "0.75rem 1.5rem",
                                    filter: "drop-shadow(0px 0px 10px rgba(255, 51, 204))",
                                }}
                            >
                                {loadingReplies ? t('forum.loadingText') : t('forum.loadMore')}
                            </button>
                        )}
                    </div>

                    <form onSubmit={handleReplySubmit}>
                        {error && <p className="error">{error}</p>}
                        <ReactQuill
                            theme="snow"
                            value={newReply}
                            onChange={setNewReply}
                            autoFocus={false}
                            modules={modules}
                            formats={formats}
                            placeholder={t('forum.subjectPage.replyFormPlaceholder')}
                            className="quill-editor"
                            style={{
                                marginTop: "1rem",
                                marginBottom: "1rem",
                                minHeight: "10rem",
                                height: "15rem"
                            }}
                        />

                        <div style={{ marginTop: "4.5rem" }}>
                            <ForumQuotaStatus
                                userId={userId}
                                subscription={userData?.subscription}
                                refreshKey={forumQuotaRefreshKey}
                            />
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: "1rem",
                                marginBottom: "1rem"
                            }}
                            className="button-container_PublierRetournerSubjectPage"
                        >
                            <button
                                type="submit"
                                disabled={isSubmittingReply}
                                className="buttonEditerSubject"
                                style={{
                                    width: "38%",
                                    marginLeft: "0.8rem"
                                }}
                            >
                                {t('forum.subjectPage.publishReply')}
                            </button>

                            <button
                                type="button"
                                onClick={handleCancel}
                                className="buttonAnnulerSubject"
                                style={{
                                    width: "38%",
                                    marginLeft: "0.8rem"
                                }}
                            >
                                {t('forum.subjectPage.returnToForum')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <div>
                <BackToTopButton />
            </div>
            <div className="footer" >
                <Footer />
            </div>
        </>
    );
};

export default SubjectPage;





// import React, { useState, useEffect, useContext } from "react";
// import { useParams, useNavigate, useLocation } from "react-router-dom";
// import axios from "axios";
// import NavigationBar from "../components/Navigation";
// import Footer from "../components/Footer";
// import UserXpAvatar from "./UserXpAvatar";
// import {
//     collection,
//     getDocs,
//     getDoc,
//     query,
//     orderBy,
//     doc,
//     updateDoc,
//     serverTimestamp,
//     increment,
//     writeBatch,
//     limit,
//     startAfter,
// } from "firebase/firestore";
// import { db } from "../config/firebasedeconfig";
// import "../styles/components/SubjectPage.scss";
// import { UserContext } from "../UserContext";
// import ReactQuill from "react-quill";
// import Quill from "quill";
// import "react-quill/dist/quill.snow.css";
// import { defaultAvatarUrl, defaultUsername, defaultEmail } from '../constants';
// import { OnlineUsersContext } from "../OnlineUsersContext";
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { getUserActivityScore, XP_WEIGHTS } from '../components/levelUtils';
// import ActivityGainToast from "./ActivityGainToast";
// import animationData_Stvalentin_pulse2 from '../lotties/st-Valentin-heart-pulse_01.json';
// import Lottie from "lottie-react";
// import BackToTopButton from '../components/BackToTopButton';
// import notificationService from '../services/notificationService';
// import { useLanguage } from '../contexts/LanguageContext';
// import { getForumQuotaStatus } from "../utils/forumQuotaUtils";
// import ForumQuotaStatus from "../components/ForumQuotaStatus";

// const REPLIES_PAGE_SIZE = 10;

// const formatActivityGainPoints = (value) => {
//     const number = Number(value) || 0;

//     return `+${Number.isInteger(number)
//         ? number
//         : number.toFixed(2).replace(/\.?0+$/, "")
//         }`;
// };

// const SubjectPage = () => {
//     const { t, currentLanguage } = useLanguage();
//     const { id } = useParams();
//     const navigate = useNavigate();
//     const location = useLocation();
//     const {
//         username,
//         userId,
//         userData,
//         messageCount,
//         updateMessageCount,
//         fetchUserMessageCount
//     } = useContext(UserContext);
//     const { onlineUsers } = useContext(OnlineUsersContext);
//     const [subject, setSubject] = useState(null);
//     const [replies, setReplies] = useState([]);
//     const [newReply, setNewReply] = useState("");
//     const [lastVisibleReply, setLastVisibleReply] = useState(null);
//     const [hasMoreReplies, setHasMoreReplies] = useState(false);
//     const [loadingReplies, setLoadingReplies] = useState(false);
//     const [error, setError] = useState("");
//     const [isHovered, setIsHovered] = useState(false);
//     const [isOnline, setIsOnline] = useState(false);
//     const [tmdbPosterPathFallback, setTmdbPosterPathFallback] = useState(null);
//     const [subjectNotFound, setSubjectNotFound] = useState(false);
//     const [activityGainToast, setActivityGainToast] = useState(null);
//     const [isSubmittingReply, setIsSubmittingReply] = useState(false);
//     const [forumQuotaRefreshKey, setForumQuotaRefreshKey] = useState(0);
//     const targetReplyId =
//         location.state?.replyId ||
//         new URLSearchParams(location.search).get("replyId") ||
//         (location.hash?.startsWith("#reply-")
//             ? location.hash.replace("#reply-", "")
//             : "");


//     // Active le mode test pourle système de titre en mettant `true` à cette variable
//     // const debugTestMode = true;
//     const debugTestMode = false;

//     /* Aspirant Figurant(0 à 9)
//     Cadreur Novice(10 à 49)
//     Apprenti Monteur(50 à 99)
//     Assistant Réalisateur(100 à 199)
//     Scénariste Prometteur(200 à 499)
//     Chef Opérateur Illumineur(500 à 999)
//     Réalisateur Émérite(1000 à 1999)
//     Maître de la Mise en Scène(2000 à 3999)
//     Légende du 7e Art(4000 à 6999)
//     Divinité de la Pellicule(7000 et +)
//      */

//     // const testMessageCount = 5;    // "Aspirant Figurant"
//     // const testMessageCount = 13;    // "Cadreur Novice" *
//     // const testMessageCount = 50;    // "Apprenti Monteur"
//     // const testMessageCount = 100;   // "Assistant Réalisateur"*
//     // const testMessageCount = 200;   // "Scénariste Prometteur"*
//     // const testMessageCount = 500;   // "Chef Opérateur Illumineur"*
//     // const testMessageCount = 1000;  // "Réalisateur Émérite"*
//     // const testMessageCount = 2000;  // "Maître de la Mise en Scène"
//     // const testMessageCount = 4000;  // "Légende du 7e Art"
//     const testMessageCount = 7500;  // "Divinité de la Pellicule"
//     const displayMessageCount = debugTestMode ? testMessageCount : messageCount;

//     const showLikeGivenActivityToast = () => {
//         setActivityGainToast({
//             id: `like-given-${Date.now()}`,
//             points: formatActivityGainPoints(XP_WEIGHTS.likesGiven),
//             unitLabel: t("profilMember.activity"),
//             label: t("activityToast.likeGiven"),
//             variant: "like",
//         });
//     };


//     const showReplyCreatedActivityToast = () => {
//         setActivityGainToast({
//             id: `reply-created-${Date.now()}`,
//             points: formatActivityGainPoints(XP_WEIGHTS.replies),
//             unitLabel: t("profilMember.activity"),
//             label: t("activityToast.replyCreated"),
//             variant: "reply",
//         });
//     };

//     // Fonction pour supprimer une réponse avec un toast de succès
//     const handleDeleteReply = async (replyId) => {
//         try {
//             const replyRef = doc(db, "posts", id, "replies", replyId);
//             const subjectRef = doc(db, "posts", id);

//             const replySnap = await getDoc(replyRef);

//             if (!replySnap.exists()) {
//                 toast.error(t('forum.subjectPage.deleteReplyError'));
//                 return;
//             }

//             const replyData = replySnap.data();
//             const replyAuthorId = replyData.authorId;
//             const replyLikes = Array.isArray(replyData.likes) ? replyData.likes : [];

//             const batch = writeBatch(db);

//             batch.delete(replyRef);

//             batch.update(subjectRef, {
//                 replies: increment(-1),
//                 lastActivityAt: serverTimestamp(),
//                 lastActivityBy: userId,
//                 lastActivityType: "reply_deleted",
//             });

//             if (replyAuthorId) {
//                 const replyAuthorRef = doc(db, "users", replyAuthorId);

//                 batch.update(replyAuthorRef, {
//                     "xp.sources.replies": increment(-1),
//                     "xp.updatedAt": serverTimestamp(),
//                 });

//                 replyLikes
//                     .filter((likedUserId) => likedUserId && likedUserId !== replyAuthorId)
//                     .forEach((likedUserId) => {
//                         const likerRef = doc(db, "users", likedUserId);

//                         batch.update(likerRef, {
//                             "xp.sources.likesGiven": increment(-1),
//                             "xp.updatedAt": serverTimestamp(),
//                         });

//                         batch.update(replyAuthorRef, {
//                             "xp.sources.likesReceived": increment(-1),
//                             "xp.updatedAt": serverTimestamp(),
//                         });
//                     });
//             }

//             await batch.commit();

//             toast.success(t('forum.subjectPage.deleteReplySuccess'), {
//                 position: toast.POSITION.BOTTOM_RIGHT,
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });

//             fetchReplies({ reset: true });
//             fetchSubject();
//         } catch (err) {
//             toast.error(t('forum.subjectPage.deleteReplyError'));
//             console.error("Erreur :", err);
//         }
//     };

//     // Fonction pour modifier une réponse avec un toast de succès
//     const handleEditReply = async (replyId, newContent) => {
//         try {
//             await updateDoc(doc(db, 'posts', id, 'replies', replyId), {
//                 content: newContent,
//                 updatedAt: new Date(),
//             });
//             toast.success(t('forum.subjectPage.editReplySuccess'), {
//                 position: toast.POSITION.BOTTOM_RIGHT,
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//             fetchReplies({ reset: true }); // Recharge les réponses après modification
//         } catch (err) {
//             toast.error(t('forum.subjectPage.editReplyError'));
//             console.error("Erreur :", err);
//         }
//     };


//     // Import des modules nécessaires pour les couleur quill
//     const Font = Quill.import("formats/font");
//     Font.whitelist = null; // Ajoutez vos polices autorisées si nécessaire
//     Quill.register(Font, true);

//     // Modules pour ReactQuill
//     const modules = {
//         toolbar: [
//             [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
//             [{ 'list': 'ordered' }, { 'list': 'bullet' }],
//             ['bold', 'italic', 'underline', 'strike', 'blockquote'],
//             [{ 'color': [] }, { 'background': [] }],
//             [{ 'align': [] }],
//             ['link', 'image', 'video'],
//             ['clean'],
//         ],
//     };

//     const formats = [
//         "header",
//         "bold",
//         "italic",
//         "underline",
//         "strike",
//         "color",
//         "background",
//         "align",
//         "link",
//         "image",
//         "video",
//     ];

//     const fetchSubject = async () => {
//         try {
//             const subjectDoc = doc(db, "posts", id);
//             const subjectSnapshot = await getDoc(subjectDoc);

//             if (!subjectSnapshot.exists()) {
//                 console.log("Le sujet n'existe pas.");
//                 setSubject(null);
//                 setReplies([]);
//                 setSubjectNotFound(true);
//                 return;
//             }

//             setSubjectNotFound(false);

//             const subjectData = {
//                 id: subjectSnapshot.id,
//                 ...subjectSnapshot.data(),
//                 likes: subjectSnapshot.data().likes || [], // Initialisez likes ici
//             };

//             // Récupérer les informations des utilisateurs ayant liké
//             subjectData.likeUsernames = await fetchLikeUsernames(subjectData.likes);

//             // Récupérer les informations de l'auteur
//             if (subjectData.authorId) {
//                 const userDocRef = doc(db, "users", subjectData.authorId);
//                 const userDoc = await getDoc(userDocRef);

//                 if (userDoc.exists()) {
//                     const userData = userDoc.data();
//                     subjectData.avatar = userData.avatar || defaultAvatarUrl;
//                     subjectData.author = userData.username || t('forum.userUnknown');
//                     const authorMessageCount = await fetchUserMessageCount(subjectData.authorId);

//                     subjectData.messageCount = authorMessageCount;
//                     subjectData.activityScore = getUserActivityScore(userData, authorMessageCount);
//                 }
//             } else {
//                 subjectData.avatar = defaultAvatarUrl;
//                 subjectData.author = t('forum.authorUnknown');
//             }

//             setSubject(subjectData);
//         } catch (error) {
//             console.error("Erreur lors de la récupération du sujet :", error);
//         }
//     };

//     useEffect(() => {
//         const scrollTimeout = window.setTimeout(() => {
//             window.scrollTo({
//                 top: 0,
//                 left: 0,
//                 behavior: "auto",
//             });
//         }, 0);

//         return () => window.clearTimeout(scrollTimeout);
//     }, [id]);

//     // Charger les détails du sujet
//     useEffect(() => {
//         fetchSubject();
//     }, [id]);

//     const enrichReplyWithAuthor = async (docSnap) => {
//         const reply = { id: docSnap.id, ...docSnap.data() };

//         if (reply.authorId) {
//             const userRef = doc(db, "users", reply.authorId);
//             const userDoc = await getDoc(userRef);

//             let replyUserData = {};

//             if (userDoc.exists()) {
//                 replyUserData = userDoc.data();
//                 reply.avatar = replyUserData.avatar || defaultAvatarUrl;
//                 reply.authorName = replyUserData.username || t('forum.userUnknown');
//             } else {
//                 reply.avatar = defaultAvatarUrl;
//                 reply.authorName = t('forum.authorUnknown');
//             }

//             const replyMessageCount = await fetchUserMessageCount(reply.authorId);

//             reply.messageCount = replyMessageCount;
//             reply.activityScore = getUserActivityScore(replyUserData, replyMessageCount);
//         } else {
//             reply.avatar = defaultAvatarUrl;
//             reply.authorName = t('forum.authorUnknown');
//             reply.messageCount = 0;
//             reply.activityScore = 0;
//         }

//         reply.likes = Array.isArray(reply.likes) ? reply.likes : [];
//         reply.likeUsernames = await fetchLikeUsernames(reply.likes);

//         return reply;
//     };

//     // Chargement de pages..
//     const fetchReplies = async ({ reset = true } = {}) => {
//         try {
//             setLoadingReplies(true);

//             const subjectRef = doc(db, "posts", id);
//             const subjectDoc = await getDoc(subjectRef);

//             if (!subjectDoc.exists()) {
//                 setSubject(null);
//                 setReplies([]);
//                 setSubjectNotFound(true);
//                 setLastVisibleReply(null);
//                 setHasMoreReplies(false);
//                 return;
//             }

//             const repliesQuery = query(
//                 collection(db, "posts", id, "replies"),
//                 orderBy("timestamp", "asc"),
//                 limit(REPLIES_PAGE_SIZE + 1)
//             );

//             const querySnapshot = await getDocs(repliesQuery);

//             if (querySnapshot.empty) {
//                 setReplies([]);
//                 setLastVisibleReply(null);
//                 setHasMoreReplies(false);
//                 return;
//             }

//             const visibleDocs = querySnapshot.docs.slice(0, REPLIES_PAGE_SIZE);
//             const hasNextPage = querySnapshot.docs.length > REPLIES_PAGE_SIZE;
//             const lastDoc = visibleDocs[visibleDocs.length - 1] || null;

//             const repliesData = await Promise.all(
//                 visibleDocs.map(enrichReplyWithAuthor)
//             );

//             setReplies(repliesData);
//             setLastVisibleReply(lastDoc);
//             setHasMoreReplies(hasNextPage);
//         } catch (error) {
//             console.error("Erreur lors de la récupération des réponses :", error);
//         } finally {
//             setLoadingReplies(false);
//         }
//     };

//     const loadMoreReplies = async () => {
//         if (!lastVisibleReply || loadingReplies) return;

//         try {
//             setLoadingReplies(true);

//             const repliesQuery = query(
//                 collection(db, "posts", id, "replies"),
//                 orderBy("timestamp", "asc"),
//                 startAfter(lastVisibleReply),
//                 limit(REPLIES_PAGE_SIZE + 1)
//             );

//             const querySnapshot = await getDocs(repliesQuery);

//             if (querySnapshot.empty) {
//                 setHasMoreReplies(false);
//                 setLastVisibleReply(null);
//                 return;
//             }

//             const visibleDocs = querySnapshot.docs.slice(0, REPLIES_PAGE_SIZE);
//             const hasNextPage = querySnapshot.docs.length > REPLIES_PAGE_SIZE;
//             const lastDoc = visibleDocs[visibleDocs.length - 1] || null;

//             const repliesData = await Promise.all(
//                 visibleDocs.map(enrichReplyWithAuthor)
//             );

//             setReplies((prevReplies) => [...prevReplies, ...repliesData]);
//             setLastVisibleReply(lastDoc);
//             setHasMoreReplies(hasNextPage);
//         } catch (error) {
//             console.error("Erreur lors du chargement des réponses suivantes :", error);
//         } finally {
//             setLoadingReplies(false);
//         }
//     };

//     useEffect(() => {
//         fetchReplies({ reset: true });
//     }, [id]);

//     useEffect(() => {
//         if (!targetReplyId || replies.length === 0) {
//             return;
//         }

//         const scrollTimeout = window.setTimeout(() => {
//             const targetElement = document.getElementById(`reply-${targetReplyId}`);

//             if (!targetElement) {
//                 return;
//             }

//             const nav = document.querySelector(".navigation");
//             const navHeight = nav ? nav.getBoundingClientRect().height : 0;
//             const extraOffset = window.innerWidth <= 992 ? 130 : 60;

//             const targetTop =
//                 targetElement.getBoundingClientRect().top +
//                 window.pageYOffset -
//                 navHeight -
//                 extraOffset;

//             window.scrollTo({
//                 top: Math.max(targetTop, 0),
//                 behavior: "smooth",
//             });

//             targetElement.classList.add("reply--notification-target");

//             window.setTimeout(() => {
//                 targetElement.classList.remove("reply--notification-target");
//             }, 3500);
//         }, 450);

//         return () => window.clearTimeout(scrollTimeout);
//     }, [targetReplyId, replies]);

//     const isMovieReviewSubject =
//         subject?.type === "movie_rating_discussion" ||
//         subject?.source === "movie_rating_review";

//     const subjectMovieId = subject?.tmdbId || subject?.movieRatingKey;

//     const subjectMoviePosterPath =
//         subject?.moviePosterPath || tmdbPosterPathFallback;

//     const subjectMoviePosterUrl = subjectMoviePosterPath
//         ? `https://image.tmdb.org/t/p/w185${subjectMoviePosterPath}`
//         : null;


//     useEffect(() => {
//         const fetchMissingMoviePoster = async () => {
//             if (!isMovieReviewSubject || !subjectMovieId || subject?.moviePosterPath) {
//                 setTmdbPosterPathFallback(null);
//                 return;
//             }

//             const normalizedMovieId = Number(subjectMovieId);

//             if (!Number.isFinite(normalizedMovieId)) {
//                 return;
//             }

//             try {
//                 const tmdbLanguage =
//                     currentLanguage === "en"
//                         ? "en-US"
//                         : `${currentLanguage}-${currentLanguage.toUpperCase()}`;

//                 const response = await axios.get(
//                     `https://api.themoviedb.org/3/movie/${normalizedMovieId}?api_key=e16900047627f56531a2d3d8d4df9e92&language=${tmdbLanguage}`
//                 );

//                 const posterPath = response.data?.poster_path || "";
//                 const backdropPath = response.data?.backdrop_path || "";
//                 const movieTitle =
//                     response.data?.title ||
//                     subject?.movieTitle ||
//                     subject?.title ||
//                     "";

//                 if (!posterPath) {
//                     return;
//                 }

//                 setTmdbPosterPathFallback(posterPath);

//                 try {
//                     await updateDoc(doc(db, "posts", id), {
//                         moviePosterPath: posterPath,
//                         movieBackdropPath: backdropPath,
//                         movieTitle,
//                         tmdbId: normalizedMovieId,
//                         movieRatingKey: String(normalizedMovieId),
//                     });
//                 } catch (updateError) {
//                     console.warn(
//                         "[MovieFrenzy] Vignette TMDB trouvée, mais impossible de l’enregistrer dans Firestore :",
//                         updateError
//                     );
//                 }
//             } catch (error) {
//                 console.error(
//                     "[MovieFrenzy] Impossible de récupérer la vignette TMDB du sujet :",
//                     error
//                 );
//             }
//         };

//         fetchMissingMoviePoster();
//     }, [
//         id,
//         isMovieReviewSubject,
//         subjectMovieId,
//         subject?.moviePosterPath,
//         subject?.movieTitle,
//         subject?.title,
//         currentLanguage,
//     ]);


//     // Ajouter une nouvelle réponse
//     const handleReplySubmit = async (e) => {
//         e.preventDefault();

//         if (!newReply || newReply === "<p><br></p>") {
//             setError(t('forum.subjectPage.errorContentRequired'));
//             return;
//         }

//         if (!userId) {
//             setError(t("forumQuota.errorUserNotFound"));
//             return;
//         }

//         if (isSubmittingReply) {
//             return;
//         }

//         setIsSubmittingReply(true);

//         try {
//             const quotaStatus = await getForumQuotaStatus({
//                 userId,
//                 subscription: userData?.subscription,
//             });

//             if (!quotaStatus.allowed) {
//                 if (quotaStatus.reason === "daily-limit") {
//                     setError(
//                         t("forumQuota.dailyLimit", {
//                             limit: quotaStatus.limits.daily,
//                         })
//                     );
//                 } else if (
//                     quotaStatus.reason === "rolling-30-days-limit"
//                 ) {
//                     setError(
//                         t("forumQuota.rolling30DaysLimit", {
//                             limit: quotaStatus.limits.rolling30Days,
//                         })
//                     );
//                 } else {
//                     setError(t("forumQuota.unavailable"));
//                 }

//                 return;
//             }
//             const replyRef = doc(
//                 collection(db, "posts", id, "replies")
//             );

//             const usageRef = doc(
//                 collection(db, "users", userId, "forumUsage")
//             );

//             const subjectRef = doc(db, "posts", id);
//             const userRef = doc(db, "users", userId);

//             const batch = writeBatch(db);

//             batch.set(replyRef, {
//                 author: username || t('forum.anonymousUser'),
//                 authorId: userId,
//                 content: newReply,
//                 timestamp: serverTimestamp(),
//                 likes: [],
//             });

//             batch.set(usageRef, {
//                 type: "reply",
//                 postId: id,
//                 replyId: replyRef.id,
//                 createdAt: serverTimestamp(),
//             });

//             batch.update(subjectRef, {
//                 replies: increment(1),
//                 lastActivityAt: serverTimestamp(),
//                 lastActivityBy: userId,
//                 lastActivityType: "reply",
//             });

//             batch.update(userRef, {
//                 "xp.sources.replies": increment(1),
//                 "xp.updatedAt": serverTimestamp(),
//             });

//             await batch.commit();
//             setForumQuotaRefreshKey((prev) => prev + 1);
//             showReplyCreatedActivityToast();
//             setNewReply("");
//             setError("");
//             fetchReplies({ reset: true });
//             fetchSubject();

//             if (userId !== subject.authorId) {
//                 const subjectTitle = subject.title;

//                 try {
//                     await notificationService.createTopicReplyNotification(
//                         subject.authorId,
//                         userId,
//                         username || t('forum.userUnknown'),
//                         id,
//                         subjectTitle
//                     );
//                     console.log("Notification de réponse créée avec succès");
//                 } catch (notifError) {
//                     console.error("Erreur lors de la création de la notification de réponse:", notifError);
//                 }
//             }
//         } catch (err) {
//             console.error(
//                 "Erreur lors de l'ajout de la réponse :",
//                 err
//             );
//         } finally {
//             setIsSubmittingReply(false);
//         }
//     };

//     // Fonction pour annuler l'ajout de la nouvelle réponse
//     const handleCancel = () => {
//         navigate("/Forum"); // Redirige vers la page Forum.js
//     };

//     // Fonction pour liker une réponse
//     const handleLike = async (replyId) => {
//         if (!userId) {
//             toast.error("Utilisateur introuvable. Veuillez vous reconnecter.");
//             return;
//         }

//         const replyRef = doc(db, "posts", id, "replies", replyId);

//         try {
//             const replyDoc = await getDoc(replyRef);

//             if (!replyDoc.exists()) {
//                 toast.error(t('forum.subjectPage.likeError'));
//                 return;
//             }

//             const replyData = replyDoc.data();
//             const replyAuthorId = replyData.authorId;
//             const currentLikes = Array.isArray(replyData.likes) ? replyData.likes : [];
//             const wasLiked = currentLikes.includes(userId);

//             const nextLikes = wasLiked
//                 ? currentLikes.filter((uid) => uid !== userId)
//                 : [...currentLikes, userId];

//             const batch = writeBatch(db);

//             batch.update(replyRef, {
//                 likes: nextLikes,
//             });

//             if (replyAuthorId && replyAuthorId !== userId) {
//                 const likerRef = doc(db, "users", userId);
//                 const replyAuthorRef = doc(db, "users", replyAuthorId);

//                 batch.update(likerRef, {
//                     "xp.sources.likesGiven": increment(wasLiked ? -1 : 1),
//                     "xp.updatedAt": serverTimestamp(),
//                 });

//                 batch.update(replyAuthorRef, {
//                     "xp.sources.likesReceived": increment(wasLiked ? -1 : 1),
//                     "xp.updatedAt": serverTimestamp(),
//                 });
//             }

//             await batch.commit();

//             if (!wasLiked && replyAuthorId && userId !== replyAuthorId) {
//                 showLikeGivenActivityToast();
//                 try {
//                     await notificationService.createReplyLikeNotification(
//                         replyAuthorId,
//                         userId,
//                         username || t('forum.userUnknown'),
//                         id,
//                         subject.title,
//                         replyId
//                     );
//                     console.log("Notification de like créée avec succès");
//                 } catch (notifError) {
//                     console.error("Erreur lors de la création de la notification de like:", notifError);
//                 }
//             }

//             fetchReplies({ reset: true });
//         } catch (err) {
//             console.error("Erreur lors du like :", err);
//             toast.error(t('forum.subjectPage.likeError'));
//         }
//     };

//     // Fonction pour liker le sujet
//     const [isLiking, setIsLiking] = useState(false);

//     const handleSubjectLike = async () => {
//         if (!userId) {
//             toast.error("Utilisateur introuvable. Veuillez vous reconnecter.");
//             return;
//         }

//         if (isLiking) return;
//         setIsLiking(true);

//         const subjectRef = doc(db, "posts", id);

//         try {
//             const subjectDoc = await getDoc(subjectRef);

//             if (!subjectDoc.exists()) {
//                 setSubject(null);
//                 setReplies([]);
//                 setSubjectNotFound(true);
//                 return;
//             }

//             setSubjectNotFound(false);

//             const subjectData = subjectDoc.data();
//             const subjectAuthorId = subjectData.authorId;
//             const currentLikes = Array.isArray(subjectData.likes) ? subjectData.likes : [];
//             const wasLiked = currentLikes.includes(userId);

//             const nextLikes = wasLiked
//                 ? currentLikes.filter((uid) => uid !== userId)
//                 : [...currentLikes, userId];

//             const batch = writeBatch(db);

//             batch.update(subjectRef, {
//                 likes: nextLikes,
//             });

//             if (subjectAuthorId && subjectAuthorId !== userId) {
//                 const likerRef = doc(db, "users", userId);
//                 const subjectAuthorRef = doc(db, "users", subjectAuthorId);

//                 batch.update(likerRef, {
//                     "xp.sources.likesGiven": increment(wasLiked ? -1 : 1),
//                     "xp.updatedAt": serverTimestamp(),
//                 });

//                 batch.update(subjectAuthorRef, {
//                     "xp.sources.likesReceived": increment(wasLiked ? -1 : 1),
//                     "xp.updatedAt": serverTimestamp(),
//                 });
//             }

//             await batch.commit();

//             if (!wasLiked && subjectAuthorId && userId !== subjectAuthorId) {
//                 showLikeGivenActivityToast();
//                 try {
//                     await notificationService.createReplyLikeNotification(
//                         subjectAuthorId,
//                         userId,
//                         username || t('forum.userUnknown'),
//                         id,
//                         subjectData.title
//                     );
//                     console.log("Notification de like de sujet créée avec succès");
//                 } catch (notifError) {
//                     console.error("Erreur lors de la création de la notification de like de sujet:", notifError);
//                 }
//             }

//             fetchSubject();
//         } catch (err) {
//             console.error("Erreur lors du like du sujet :", err);
//             toast.error(t('forum.subjectPage.likeError'));
//         } finally {
//             setIsLiking(false);
//         }
//     };


//     // Fonction pour récupérer les noms des utilisateurs ayant liké le sujet
//     const fetchLikeUsernames = async (likeIds) => {
//         if (!likeIds || !Array.isArray(likeIds) || likeIds.length === 0) return [];
//         return await Promise.all(
//             likeIds
//                 .filter((userId) => userId != null) // On élimine les valeurs nulles ou undefined
//                 .map(async (userId) => {
//                     try {
//                         const userRef = doc(db, "users", userId);
//                         const userDoc = await getDoc(userRef);
//                         return userDoc.exists() ? userDoc.data().username : t('forum.userUnknown');
//                     } catch (err) {
//                         console.error(err);
//                         return t('forum.userUnknown');
//                     }
//                 })
//         );
//     };

//     if (subjectNotFound) {
//         return (
//             <>
//                 <ToastContainer />
//                 <div
//                     className="forum-page"
//                     style={{
//                         backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
//                         backgroundSize: "cover",
//                         backgroundPosition: "center",
//                         backgroundRepeat: "no-repeat",
//                         backgroundAttachment: "fixed",
//                         backgroundBlendMode: "overlay",
//                         backgroundColor: "#200211",
//                         opacity: "0.9",
//                         overflowX: "hidden",
//                         overflowY: "auto",
//                         minHeight: "100vh",
//                     }}
//                 >
//                     <NavigationBar />

//                     <ActivityGainToast
//                         visible={Boolean(activityGainToast)}
//                         toastId={activityGainToast?.id}
//                         points={activityGainToast?.points}
//                         unitLabel={activityGainToast?.unitLabel}
//                         label={activityGainToast?.label}
//                         variant={activityGainToast?.variant}
//                         onDone={() => setActivityGainToast(null)}
//                     />

//                     <div className="subject-page">
//                         <div className="subject-not-found">
//                             <h2>Ce sujet n’existe plus ou a été supprimé.</h2>
//                             <p>Il n’est plus possible d’afficher cette discussion.</p>

//                             <button
//                                 type="button"
//                                 className="subject-not-found__button"
//                                 onClick={() => navigate("/Forum")}
//                             >
//                                 Retour au forum
//                             </button>
//                         </div>
//                     </div>
//                 </div>

//                 <div>
//                     <BackToTopButton />
//                 </div>

//                 <div className="footer">
//                     <Footer />
//                 </div>
//             </>
//         );
//     }

//     return (
//         <>
//             <ToastContainer />

//             <ActivityGainToast
//                 visible={Boolean(activityGainToast)}
//                 toastId={activityGainToast?.id}
//                 points={activityGainToast?.points}
//                 unitLabel={activityGainToast?.unitLabel}
//                 label={activityGainToast?.label}
//                 variant={activityGainToast?.variant}
//                 onDone={() => setActivityGainToast(null)}
//             />

//             <div
//                 className="forum-page"
//                 style={{
//                     backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
//                     backgroundSize: "cover",
//                     backgroundPosition: "center",
//                     backgroundRepeat: "no-repeat",
//                     backgroundAttachment: "fixed",
//                     backgroundBlendMode: "overlay",
//                     backgroundColor: "#200211",
//                     opacity: "0.9",
//                     overflowX: "hidden",
//                     overflowY: "auto",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <NavigationBar />

//                 <div className="subject-page"
//                     style={{
//                         marginTop: "1.25rem",
//                         padding: "1.5rem",
//                         backgroundColor: "rgba(255, 255, 255, 0.1)",
//                         borderRadius: "0.5rem",
//                         boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
//                         border: "2px solid #00FFFF",

//                     }}
//                 >
//                     {subject ? (
//                         <div className="subject-container"
//                             style={{
//                                 marginBottom: "1.5rem",
//                                 padding: "1.5rem",
//                                 backgroundColor: "rgba(0, 0, 0, 0.6)", // Fond plus opaque pour une meilleure lisibilité
//                                 borderRadius: "0.5rem",
//                                 boxShadow:
//                                     "0 0.125rem 0.5rem rgba(0, 0, 0, 0.7), inset 0 0.125rem 0.25rem rgba(0, 0, 0, 0.2)", // Ombre externe et ombre intérieure plus discrète
//                                 border: "2px solid #00FFFF", // Bordure unifiée en #00FFFF
//                             }}
//                         >
//                             <div className="subject-movie-review-header">
//                                 <div className="subject-movie-review-header__text">
//                                     <h1>{subject.title}</h1>
//                                     <p dangerouslySetInnerHTML={{ __html: subject.content }}></p>
//                                 </div>

//                                 {isMovieReviewSubject && subjectMovieId && subjectMoviePosterUrl && (
//                                     <button
//                                         type="button"
//                                         className="subject-movie-card-link"
//                                         onClick={() => navigate(`/ficheFilm/${subjectMovieId}`)}
//                                         title={`Retourner à la fiche de ${subject.movieTitle || subject.title}`}
//                                     >
//                                         <img
//                                             src={subjectMoviePosterUrl}
//                                             alt={subject.movieTitle || subject.title}
//                                             className="subject-movie-card-link__poster"
//                                         />
//                                         <span className="subject-movie-card-link__label">
//                                             Voir la fiche
//                                         </span>
//                                     </button>
//                                 )}
//                             </div>
//                             <div className="subject-author"
//                                 style={{
//                                     display: "flex",
//                                     alignItems: "center",
//                                     marginTop: "0.5rem",
//                                     gap: "0.5rem",
//                                     flexDirection: "row",
//                                     flexWrap: "wrap",
//                                     justifyContent: "space-between",
//                                     backgroundColor: "rgba(0, 0, 0, 0.7)", // Fond un peu plus foncé pour accentuer le contraste avec le texte
//                                     padding: "0.75rem",
//                                     borderRadius: "0.5rem",
//                                     border: "1px solid #00FFFF",
//                                 }}
//                             >
//                                 <div className="avatar-containerForumInfo" style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: "1" }}>
//                                     <span><p>{t('forum.subjectPage.publishedBy')}{" "}<span style={{ fontWeight: "bold", color: "#00FFFF", marginLeft: "0.25rem" }}>{subject.author}</span></p></span>
//                                     <span><p>{t('forum.subjectPage.at')}{" "}<span style={{ fontWeight: "bold", color: "#FF33CC", marginLeft: "0.25rem" }}>{new Date(subject.timestamp?.seconds * 1000).toLocaleString()}</span></p></span>
//                                     {/* Bouton de like */}
//                                     <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
//                                         <button
//                                             className={`heart-buttonReply ${subject.likes && subject.likes.includes(userId) ? "liked" : ""}`}
//                                             onClick={handleSubjectLike}
//                                             style={{
//                                                 display: "flex",
//                                                 alignItems: "center",
//                                                 justifyContent: "center",
//                                                 gap: "0.5rem",
//                                                 fontSize: "1.5rem",
//                                                 marginTop: "0.5rem",
//                                                 backgroundColor: "rgba(255, 255, 255, 0.1)",
//                                                 borderRadius: "0.5rem",
//                                                 padding: "0.5rem",
//                                                 boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
//                                                 border: "2px solid #D90093"
//                                             }}
//                                         >
//                                             <Lottie animationData={animationData_Stvalentin_pulse2} style={{ width: "3.5rem", height: "3.5rem" }} /> <span style={{ fontWeight: "bold", color: "#00FFFF", marginRight: "0.5rem" }}>{subject.likes ? subject.likes.length : 0}</span>
//                                         </button>
//                                     </div>
//                                     {subject.likeUsernames && subject.likeUsernames.length > 0 && (
//                                         <div className="like-list" style={{ marginTop: "0.5rem" }}>
//                                             <p style={{ fontWeight: "bold", color: "#00FFFF", marginLeft: "0.25rem" }}>{t('forum.subjectPage.likedBy')}{" "}<span style={{ fontWeight: "Light!", color: "#FFFFFF", marginLeft: "0.25rem", textShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)" }}>{subject.likeUsernames.join(", ")}</span></p>
//                                         </div>
//                                     )}
//                                 </div>
//                                 <div className="avatar-containerForumAvatarInfo" style={{ color: "#00FFFF", fontSize: "0.8rem", backgroundColor: "#200211", padding: "0.5rem", borderRadius: "0.25rem", boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)", flex: "1", border: "2px solid #D90093", minWidth: "50vw", width: "50vw" }}>
//                                     <div className="avatar-containerForumHome"
//                                         style={{
//                                             display: "flex",
//                                             alignItems: "center",
//                                             marginTop: "0.5rem",
//                                             gap: "0.5rem",
//                                             flexDirection: "row",
//                                         }}
//                                     >
//                                         <UserXpAvatar
//                                             avatarUrl={subject.avatar || defaultAvatarUrl}
//                                             activityScore={
//                                                 debugTestMode
//                                                     ? testMessageCount
//                                                     : (subject.activityScore ?? subject.messageCount ?? 0)
//                                             }
//                                             fallbackActivityScore={subject.messageCount}
//                                             titleLabel={t("forum.levelTitle")}
//                                             activityLabel={t("forum.activityScore")}
//                                             className="user-xp-avatar--subject-main"
//                                             userId={subject.authorId}
//                                         />
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     ) : (
//                         <p>{t('forum.subjectPage.loading')}</p>
//                     )}
//                     <div className="replies">
//                         <h2>{t('forum.subjectPage.repliesTitle')}</h2>
//                         {/* Afficher les réponses les plus anciennes en premier */}
//                         {replies.map(reply => (
//                             <div
//                                 key={reply.id}
//                                 id={`reply-${reply.id}`}
//                                 className={`reply ${targetReplyId === reply.id ? "reply--targeted-by-notification" : ""}`}
//                                 style={{
//                                     backgroundColor: "rgba(0, 0, 0, 0.6)",
//                                     padding: "1rem",
//                                     marginBottom: "1rem",
//                                     borderRadius: "0.5rem",
//                                     boxShadow:
//                                         "0 0.125rem 0.5rem rgba(0, 0, 0, 0.7), inset 0 0.125rem 0.25rem rgba(0, 0, 0, 0.2)",
//                                     border: "2px solid #00FFFF",
//                                 }}
//                             >
//                                 <div className="reply-author">
//                                     <p dangerouslySetInnerHTML={{ __html: reply.content }}></p>
//                                     {reply.updatedAt && (
//                                         <small style={{ fontStyle: 'italic', color: 'red', marginBottom: "0.5rem", display: "block" }}>
//                                             {t('forum.subjectPage.modifiedOn')}{" "}{new Date(reply.updatedAt.seconds * 1000).toLocaleString()}
//                                         </small>
//                                     )}
//                                     <div
//                                         className="reply-author_description"
//                                         style={{
//                                             display: "flex",
//                                             alignItems: "center",
//                                             marginTop: "0.5rem",
//                                             backgroundColor: "rgba(0, 0, 0, 0.4)",
//                                             borderRadius: "0.5rem",
//                                             padding: "0.5rem",
//                                             boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5), inset 0 0.125rem 0.25rem #00FFFF",
//                                             border: "1px solid #00FFFF"
//                                         }}
//                                     >
//                                         <span style={{ display: "flex", flexDirection: "column", marginLeft: "0.5rem", flex: "1" }}>
//                                             <p>
//                                                 {t('forum.subjectPage.publishedBy')}{" "}
//                                                 <span style={{ fontWeight: "bold", color: "#00FFFF", marginLeft: "0.25rem" }}>
//                                                     {reply.author}
//                                                 </span>
//                                             </p>
//                                             <p>
//                                                 {t('forum.subjectPage.at')}{" "}
//                                                 <span style={{ fontWeight: "bold", color: "#FF33CC", marginLeft: "0.25rem" }}>
//                                                     {new Date(reply.timestamp?.seconds * 1000).toLocaleString()}
//                                                 </span>
//                                             </p>
//                                             {/* Bouton de like */}
//                                             <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
//                                                 <button
//                                                     className={`heart-buttonReply ${reply.likes && reply.likes.includes(userId) ? "liked" : ""}`}
//                                                     onClick={() => handleLike(reply.id)}
//                                                     style={{
//                                                         display: "flex",
//                                                         alignItems: "center",
//                                                         justifyContent: "center",
//                                                         gap: "0.5rem",
//                                                         fontSize: "1.5rem",
//                                                         marginTop: "0.5rem",
//                                                         backgroundColor: "rgba(255, 255, 255, 0.1)",
//                                                         borderRadius: "0.5rem",
//                                                         padding: "0.5rem",
//                                                         boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
//                                                         border: "2px solid #D90093"
//                                                     }}
//                                                 >
//                                                     <Lottie animationData={animationData_Stvalentin_pulse2} style={{ width: "3.5rem", height: "3.5rem" }} /> <span style={{ fontWeight: "bold", color: "#00FFFF", marginRight: "0.5rem" }}>{reply.likes ? reply.likes.length : 0}</span>
//                                                 </button>
//                                             </div>
//                                             {reply.likeUsernames && reply.likeUsernames.length > 0 && (
//                                                 <div className="like-list" style={{ marginTop: "0.5rem" }}>
//                                                     <p style={{ fontWeight: "bold", color: "#00FFFF", marginLeft: "0.25rem" }}>{t('forum.subjectPage.likedBy')}{" "}<span style={{ fontWeight: "Light", color: "#FFFFFF", marginLeft: "0.25rem", textShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)" }}>{reply.likeUsernames.join(", ")}</span></p>
//                                                 </div>
//                                             )}
//                                         </span>
//                                         {reply.authorId === userId && (
//                                             <div
//                                                 className="reply-actionsSubjectPage"
//                                                 style={{
//                                                     display: "flex",
//                                                     alignItems: "center",
//                                                     justifyContent: "center",
//                                                     marginRight: "2.5rem",
//                                                     flex: "0.575"
//                                                 }}
//                                             >
//                                                 <button
//                                                     onClick={() => handleDeleteReply(reply.id)}
//                                                     style={{ marginRight: "0.5rem" }}
//                                                 >
//                                                     {t('forum.delete')}
//                                                 </button>
//                                                 <button
//                                                     onClick={() => navigate(`/edit-reply/${id}/${reply.id}`)}
//                                                     style={{ marginRight: "0.5rem" }}
//                                                 >
//                                                     {t('forum.edit')}
//                                                 </button>
//                                             </div>
//                                         )}
//                                         <div
//                                             className="base_avatar-containerForumHome"
//                                             style={{
//                                                 color: "#00FFFF",
//                                                 fontSize: "0.8rem",
//                                                 backgroundColor: "#200211",
//                                                 padding: "0.5rem",
//                                                 borderRadius: "0.25rem",
//                                                 boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
//                                                 flex: "1",
//                                                 minWidth: "15rem",
//                                                 border: "1px solid #00FFFF"
//                                             }}
//                                         >
//                                             <div
//                                                 className="avatar-containerForumHomeImg"
//                                                 style={{
//                                                     display: "flex",
//                                                     alignItems: "center",
//                                                     marginTop: "0.5rem",
//                                                     gap: "0.5rem",
//                                                     flexDirection: "row"
//                                                 }}
//                                             >
//                                                 <UserXpAvatar
//                                                     avatarUrl={reply.avatar || defaultAvatarUrl}
//                                                     activityScore={
//                                                         debugTestMode
//                                                             ? testMessageCount
//                                                             : (reply.activityScore ?? reply.messageCount ?? 0)
//                                                     }
//                                                     fallbackActivityScore={reply.messageCount}
//                                                     titleLabel={t("forum.levelTitle")}
//                                                     activityLabel={t("forum.activityScore")}
//                                                     className="user-xp-avatar--subject-reply"
//                                                     userId={reply.authorId}
//                                                 />
//                                             </div>
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         ))}
//                         {hasMoreReplies && (
//                             <button
//                                 type="button"
//                                 onClick={loadMoreReplies}
//                                 disabled={loadingReplies}
//                                 className="buttonForumChargerMore"
//                                 style={{
//                                     display: "block",
//                                     margin: "1.5rem auto",
//                                     padding: "0.75rem 1.5rem",
//                                     filter: "drop-shadow(0px 0px 10px rgba(255, 51, 204))",
//                                 }}
//                             >
//                                 {loadingReplies ? t('forum.loadingText') : t('forum.loadMore')}
//                             </button>
//                         )}
//                     </div>

//                     <form onSubmit={handleReplySubmit}>
//                         {error && <p className="error">{error}</p>}
//                         <ReactQuill
//                             theme="snow"
//                             value={newReply}
//                             onChange={setNewReply}
//                             autoFocus={false}
//                             modules={modules}
//                             formats={formats}
//                             placeholder={t('forum.subjectPage.replyFormPlaceholder')}
//                             className="quill-editor"
//                             style={{
//                                 marginTop: "1rem",
//                                 marginBottom: "1rem",
//                                 minHeight: "10rem",
//                                 height: "15rem"
//                             }}
//                         />

//                         <div style={{ marginTop: "4.5rem" }}>
//                             <ForumQuotaStatus
//                                 userId={userId}
//                                 subscription={userData?.subscription}
//                                 refreshKey={forumQuotaRefreshKey}
//                             />
//                         </div>

//                         <div
//                             style={{
//                                 display: "flex",
//                                 justifyContent: "space-between",
//                                 marginTop: "1rem",
//                                 marginBottom: "1rem"
//                             }}
//                             className="button-container_PublierRetournerSubjectPage"
//                         >
//                             <button
//                                 type="submit"
//                                 disabled={isSubmittingReply}
//                                 className="buttonEditerSubject"
//                                 style={{
//                                     width: "38%",
//                                     marginLeft: "0.8rem"
//                                 }}
//                             >
//                                 {t('forum.subjectPage.publishReply')}
//                             </button>

//                             <button
//                                 type="button"
//                                 onClick={handleCancel}
//                                 className="buttonAnnulerSubject"
//                                 style={{
//                                     width: "38%",
//                                     marginLeft: "0.8rem"
//                                 }}
//                             >
//                                 {t('forum.subjectPage.returnToForum')}
//                             </button>
//                         </div>
//                     </form>
//                 </div>
//             </div>
//             <div>
//                 <BackToTopButton />
//             </div>
//             <div className="footer" >
//                 <Footer />
//             </div>
//         </>
//     );
// };

// export default SubjectPage;




